import { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { PlanAuditResultCard } from './PlanAuditResultCard';
import { usePlanStore } from '@/stores/ai/plan-store';
import { formatDuration } from '@/utils';
import {
  FOREST_GREEN, MID_GRAY, ERROR, WARNING, BORDER, WHITE,
} from '@/styles/tokens';
import {
  CheckCircle2, AlertTriangle, XCircle, ChevronDown, ChevronRight,
  Clock, Layers, BarChart3, AlertOctagon, Copy, Check,
} from 'lucide-react';
import { PLAN_DOMAIN_LABELS } from '@/types/plan';
import type { PhasePrompt, AuditResult } from '@/types/plan';

/* ── Local helpers ── */

function verdictColor(verdict: 'pass' | 'warn' | 'fail'): string {
  switch (verdict) {
    case 'pass': return FOREST_GREEN.DEFAULT;
    case 'warn': return WARNING.DEFAULT;
    case 'fail': return ERROR.DEFAULT;
  }
}

function camelToTitle(key: string): string {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim();
}

const STAGE_LABELS: Record<string, string> = {
  researching: 'Research',
  generating_roadmap: 'Roadmap Generation',
  auditing_roadmap: 'Roadmap Audit',
  decomposing: 'Phase Decomposition',
  auditing_phases: 'Phase Audits',
  reviewing: 'Review',
  executing_phase: 'Execution',
  auditing_implementation: 'Implementation Audits',
  final_audit: 'Final Audit',
};

/* ── Collapsible section ── */

function CollapsibleSection({ title, sectionKey, icon, expanded, onToggle, children }: {
  title: string;
  sectionKey: string;
  icon?: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <button
        type="button"
        className="flex items-center gap-2 w-full"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={`details-section-${sectionKey}`}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0', textAlign: 'left' }}
      >
        {icon}
        <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: '#1F2937' }}>{title}</span>
        {expanded
          ? <ChevronDown className="w-4 h-4" style={{ color: MID_GRAY.DEFAULT }} />
          : <ChevronRight className="w-4 h-4" style={{ color: MID_GRAY.DEFAULT }} />}
      </button>
      {expanded && (
        <div id={`details-section-${sectionKey}`} style={{ paddingTop: 4 }}>
          {children}
        </div>
      )}
    </div>
  );
}

/* ── Phase row ── */

function PhaseRow({ phase, audit, elapsedMs, error }: {
  phase: PhasePrompt;
  audit?: AuditResult;
  elapsedMs?: number;
  error?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const hasCriteria = audit && audit.criteria.length > 0;

  return (
    <div style={{
      padding: '10px 12px',
      borderRadius: 8,
      border: `1px solid ${error ? `${ERROR.DEFAULT}30` : BORDER.DEFAULT}`,
      background: error ? ERROR.bg : WHITE,
    }}>
      {/* Header row — clickable when audit criteria exist */}
      <button
        type="button"
        className="flex items-center gap-2 w-full"
        onClick={() => hasCriteria && setExpanded(!expanded)}
        aria-expanded={hasCriteria ? expanded : undefined}
        style={{ background: 'none', border: 'none', cursor: hasCriteria ? 'pointer' : 'default', padding: 0, textAlign: 'left' }}
      >
        <span style={{
          fontSize: 11, fontWeight: 700, color: WHITE,
          background: FOREST_GREEN.DEFAULT, borderRadius: '50%',
          width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          {phase.phaseNumber}
        </span>
        <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: '#1F2937' }}>{phase.title}</span>
        <span style={{
          fontSize: 10, padding: '1px 6px', borderRadius: 4,
          background: `${FOREST_GREEN.DEFAULT}10`, color: FOREST_GREEN.DEFAULT, fontWeight: 500,
        }}>
          {PLAN_DOMAIN_LABELS[phase.domain] ?? phase.domain}
        </span>
        {audit && <PlanAuditResultCard auditResult={audit} auditType="" compact />}
        {hasCriteria && (
          expanded
            ? <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" style={{ color: MID_GRAY.DEFAULT }} />
            : <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color: MID_GRAY.DEFAULT }} />
        )}
      </button>

      {/* Meta row */}
      <div className="flex items-center gap-3" style={{ marginTop: 4, fontSize: 11, color: MID_GRAY.DEFAULT }}>
        {elapsedMs != null && (
          <span className="inline-flex items-center gap-1" style={{ fontVariantNumeric: 'tabular-nums' }}>
            <Clock style={{ width: 11, height: 11 }} />
            {formatDuration(elapsedMs)}
          </span>
        )}
        {phase.pageName && <span>Page: {phase.pageName}</span>}
      </div>

      {/* Error */}
      {error && (
        <div style={{ marginTop: 6, fontSize: 11, color: ERROR.DEFAULT }}>
          Error: {error}
        </div>
      )}

      {/* Expanded audit criteria */}
      {expanded && audit && (
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: `1px solid ${BORDER.DEFAULT}` }}>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {audit.criteria.map((c) => {
              const cColor = verdictColor(c.verdict);
              const bgTint = c.verdict === 'pass'
                ? `${FOREST_GREEN.DEFAULT}06`
                : c.verdict === 'warn' ? `${WARNING.DEFAULT}10` : `${ERROR.DEFAULT}10`;
              const borderTint = c.verdict === 'pass'
                ? `${FOREST_GREEN.DEFAULT}18`
                : c.verdict === 'warn' ? `${WARNING.DEFAULT}25` : `${ERROR.DEFAULT}25`;
              // Use structured feedback when available, fall back to sentence-splitting reasoning
              const bullets: Array<{ text: string; sentiment: string }> =
                c.feedback && c.feedback.length > 0
                  ? c.feedback
                  : c.reasoning
                    ? c.reasoning.split(/(?<=[.!?])\s+/).filter((s) => s.trim().length > 0).map(s => ({ text: s, sentiment: 'neutral' }))
                    : [];
              const sentimentColor = (s: string) =>
                s === 'positive' ? MID_GRAY.DEFAULT
                : s === 'negative' ? WARNING.DEFAULT
                : MID_GRAY.lighter;
              return (
                <li key={c.name} style={{
                  fontSize: 12, padding: '8px 10px', borderRadius: 6,
                  background: bgTint, border: `1px solid ${borderTint}`,
                }}>
                  <div className="flex items-center gap-2" style={{ marginBottom: 4 }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: cColor, flexShrink: 0 }} />
                    <span style={{ fontWeight: 600, color: '#1F2937' }}>{c.name}</span>
                    <span style={{ fontWeight: 600, color: cColor, fontVariantNumeric: 'tabular-nums', marginLeft: 'auto' }}>
                      {c.score}/100
                    </span>
                  </div>
                  {bullets.length > 0 && (
                    <ul style={{ margin: '0 0 0 14px', padding: '0 0 0 16px', fontSize: 11, listStyleType: 'disc' }}>
                      {bullets.map((b, i) => (
                        <li key={i} style={{ color: sentimentColor(b.sentiment), lineHeight: 1.5, marginBottom: 2 }}>{b.text}</li>
                      ))}
                    </ul>
                  )}
                  {c.suggestions.length > 0 && (
                    <ul style={{ margin: '4px 0 0 14px', padding: '0 0 0 16px', fontSize: 11, listStyleType: 'circle' }}>
                      {c.suggestions.map((s, i) => (
                        <li key={i} style={{ color: WARNING.DEFAULT, lineHeight: 1.5, marginBottom: 2 }}>{s}</li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Phase-level issues */}
          {audit.blockingIssues.length > 0 && (
            <div style={{ marginTop: 8, background: ERROR.bg, borderRadius: 6, padding: '6px 10px', fontSize: 11 }}>
              <span style={{ fontWeight: 600, color: ERROR.DEFAULT }}>Blocking Issues</span>
              <ul style={{ margin: '2px 0 0 14px', padding: '0 0 0 12px' }}>
                {audit.blockingIssues.map((issue, i) => (
                  <li key={i} style={{ color: ERROR.DEFAULT, lineHeight: 1.4 }}>{issue}</li>
                ))}
              </ul>
            </div>
          )}
          {audit.warnings.length > 0 && (
            <div style={{ marginTop: 4, background: `${WARNING.DEFAULT}15`, borderRadius: 6, padding: '6px 10px', fontSize: 11 }}>
              <span style={{ fontWeight: 600, color: WARNING.DEFAULT }}>Warnings</span>
              <ul style={{ margin: '2px 0 0 14px', padding: '0 0 0 12px' }}>
                {audit.warnings.map((w, i) => (
                  <li key={i} style={{ color: WARNING.DEFAULT, lineHeight: 1.4 }}>{w}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Main modal ── */

interface PlanDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function PlanDetailsModal({ isOpen, onClose }: PlanDetailsModalProps) {
  const planName = usePlanStore((s) => s.planName);
  const finalAudit = usePlanStore((s) => s.finalAudit);
  const phaseAudits = usePlanStore((s) => s.phaseAudits);
  const implementationAudits = usePlanStore((s) => s.implementationAudits);
  const phases = usePlanStore((s) => s.phases);
  const stageTimings = usePlanStore((s) => s.stageTimings);
  const subStepTimings = usePlanStore((s) => s.subStepTimings);
  const executionErrors = usePlanStore((s) => s.executionErrors);
  const artifactCounts = usePlanStore((s) => s.artifactCounts);
  const totalLLMCalls = usePlanStore((s) => s.totalLLMCalls);
  const executionTimeMs = usePlanStore((s) => s.executionTimeMs);
  const pipelineMode = usePlanStore((s) => s.pipelineMode);

  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['criteria', 'phases']),
  );
  const [copied, setCopied] = useState(false);

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Derive verdict from score
  const scoreVerdict = finalAudit
    ? finalAudit.overallScore >= 80 ? 'pass' as const : finalAudit.overallScore >= 50 ? 'warn' as const : 'fail' as const
    : null;

  // Stage timing entries (completed stages only)
  const stageEntries = Object.entries(stageTimings)
    .filter(([, t]) => t.endedAt != null)
    .map(([key, t]) => ({
      key,
      label: STAGE_LABELS[key] ?? camelToTitle(key),
      durationMs: t.endedAt! - t.startedAt,
    }));

  // Aggregate sub-step timings per phase
  const phaseTimingMap = new Map<string, number>();
  for (const [compositeKey, timing] of Object.entries(subStepTimings)) {
    const phaseId = compositeKey.split(':')[0];
    if (timing.endedAt == null) continue;
    const elapsed = timing.endedAt - timing.startedAt;
    phaseTimingMap.set(phaseId, (phaseTimingMap.get(phaseId) ?? 0) + elapsed);
  }

  // Issues count
  const issueCount = finalAudit
    ? finalAudit.blockingIssues.length + finalAudit.warnings.length + finalAudit.suggestions.length
    : 0;

  // Total artifacts
  const totalArtifacts = artifactCounts
    ? Object.values(artifactCounts).reduce((a, b) => a + b, 0)
    : 0;

  const buildReportText = (): string => {
    const lines: string[] = [];
    lines.push(`# ${planName || 'Plan Complete'}`);
    if (finalAudit) lines.push(`Score: ${finalAudit.overallScore}/100 (${finalAudit.overallScore >= 80 ? 'PASS' : finalAudit.overallScore >= 50 ? 'WARN' : 'FAIL'})`);
    lines.push(`Duration: ${formatDuration(executionTimeMs)}`);
    lines.push(`Mode: ${pipelineMode} | LLM Calls: ${totalLLMCalls} | Artifacts: ${totalArtifacts}`);
    if (artifactCounts && Object.keys(artifactCounts).length > 0) {
      lines.push(`Breakdown: ${Object.entries(artifactCounts).map(([k, v]) => `${camelToTitle(k)}: ${v}`).join(', ')}`);
    }

    if (finalAudit) {
      lines.push('', '## Audit Criteria');
      for (const c of finalAudit.criteria) {
        lines.push(`\n### ${c.name} — ${c.score}/100 (${c.verdict})`);
        const bullets = c.feedback?.length ? c.feedback : c.reasoning
          ? c.reasoning.split(/(?<=[.!?])\s+/).filter(s => s.trim()).map(s => ({ text: s, sentiment: 'neutral' as const }))
          : [];
        for (const b of bullets) {
          const tag = b.sentiment !== 'neutral' ? ` [${b.sentiment}]` : '';
          lines.push(`  - ${b.text}${tag}`);
        }
        if (c.suggestions.length > 0) {
          for (const s of c.suggestions) lines.push(`  * ${s}`);
        }
      }
    }

    if (phases.length > 0) {
      lines.push('', '## Phase Timeline');
      for (const phase of phases) {
        const elapsed = phaseTimingMap.get(phase.id);
        lines.push(`\n### Phase ${phase.phaseNumber}: ${phase.title} (${phase.domain})`);
        if (elapsed) lines.push(`  Duration: ${formatDuration(elapsed)}`);
        const audit = implementationAudits[phase.id] ?? phaseAudits[phase.id];
        if (audit) {
          lines.push(`  Audit: ${audit.overallScore}/100 (${audit.verdict})`);
          for (const c of audit.criteria) {
            lines.push(`  - ${c.name}: ${c.score}/100 (${c.verdict})`);
            const bullets = c.feedback?.length ? c.feedback : c.reasoning
              ? c.reasoning.split(/(?<=[.!?])\s+/).filter(s => s.trim()).map(s => ({ text: s, sentiment: 'neutral' as const }))
              : [];
            for (const b of bullets) {
              const tag = b.sentiment !== 'neutral' ? ` [${b.sentiment}]` : '';
              lines.push(`    - ${b.text}${tag}`);
            }
          }
          if (audit.blockingIssues.length > 0) {
            lines.push('  Blocking Issues:');
            for (const issue of audit.blockingIssues) lines.push(`    ! ${issue}`);
          }
          if (audit.warnings.length > 0) {
            lines.push('  Warnings:');
            for (const w of audit.warnings) lines.push(`    ~ ${w}`);
          }
        }
        const error = executionErrors[phase.id];
        if (error) lines.push(`  ERROR: ${error}`);
      }
    }

    if (stageEntries.length > 0) {
      lines.push('', '## Stage Timings');
      for (const { label, durationMs } of stageEntries) {
        lines.push(`  ${label}: ${formatDuration(durationMs)}`);
      }
    }

    if (finalAudit && issueCount > 0) {
      lines.push('', '## Issues & Suggestions');
      if (finalAudit.blockingIssues.length > 0) {
        lines.push('Blocking Issues:');
        for (const issue of finalAudit.blockingIssues) lines.push(`  ! ${issue}`);
      }
      if (finalAudit.warnings.length > 0) {
        lines.push('Warnings:');
        for (const w of finalAudit.warnings) lines.push(`  ~ ${w}`);
      }
      if (finalAudit.suggestions.length > 0) {
        lines.push('Suggestions:');
        for (const s of finalAudit.suggestions) lines.push(`  - ${s}`);
      }
    }

    return lines.join('\n');
  };

  const copyReport = async () => {
    await navigator.clipboard.writeText(buildReportText());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent variant="light" className="sm:max-w-4xl p-0 gap-0">
        <div className="flex flex-col" style={{ height: '60vh' }}>

          {/* Header */}
          <div className="flex items-center justify-between px-5 pt-4 pb-3"
            style={{ borderBottom: `1px solid ${BORDER.DEFAULT}` }}
          >
            <div className="flex items-center gap-3" style={{ flex: 1 }}>
              <CheckCircle2 className="w-5 h-5" style={{ color: FOREST_GREEN.DEFAULT }} />
              <span style={{ fontSize: 16, fontWeight: 600, color: '#1F2937' }}>
                {planName || 'Plan Complete'}
              </span>
              {finalAudit && scoreVerdict && (
                <span style={{
                  fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                  color: WHITE, backgroundColor: verdictColor(scoreVerdict),
                  padding: '2px 10px', borderRadius: 4,
                }}>
                  {finalAudit.overallScore}/100
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span style={{
                fontSize: 12, color: MID_GRAY.DEFAULT, fontVariantNumeric: 'tabular-nums',
              }}>
                {formatDuration(executionTimeMs)}
              </span>
              <button
                type="button"
                onClick={copyReport}
                title="Copy full report"
                style={{
                  background: 'none', border: `1px solid ${BORDER.DEFAULT}`, borderRadius: 6,
                  padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                  fontSize: 11, color: copied ? FOREST_GREEN.DEFAULT : MID_GRAY.DEFAULT,
                  transition: 'color 0.2s',
                }}
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>

          {/* Stats bar */}
          <div
            className="flex items-center gap-5"
            style={{
              padding: '8px 20px', fontSize: 12, color: MID_GRAY.DEFAULT,
              borderBottom: `1px solid ${BORDER.DEFAULT}`,
            }}
          >
            {totalArtifacts > 0 && (
              <span><strong style={{ color: '#1F2937' }}>{totalArtifacts}</strong> artifacts</span>
            )}
            <span><strong style={{ color: '#1F2937', fontVariantNumeric: 'tabular-nums' }}>{totalLLMCalls}</strong> LLM calls</span>
            <span style={{ textTransform: 'capitalize' }}>{pipelineMode} mode</span>
            {artifactCounts && Object.keys(artifactCounts).length > 0 && (
              <span style={{ marginLeft: 'auto', fontSize: 11, color: MID_GRAY.lighter }}>
                {Object.entries(artifactCounts).map(([k, v]) => `${camelToTitle(k)}: ${v}`).join(' · ')}
              </span>
            )}
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto" style={{ padding: '16px 20px' }}>

            {/* Audit Criteria */}
            {finalAudit && (
              <CollapsibleSection
                title="Audit Criteria"
                sectionKey="criteria"
                icon={<BarChart3 className="w-4 h-4" style={{ color: MID_GRAY.DEFAULT }} />}
                expanded={expandedSections.has('criteria')}
                onToggle={() => toggleSection('criteria')}
              >
                <PlanAuditResultCard auditResult={finalAudit} auditType="Final Audit" />
              </CollapsibleSection>
            )}

            {/* Phase Timeline */}
            {phases.length > 0 && (
              <CollapsibleSection
                title={`Phase Timeline (${phases.length})`}
                sectionKey="phases"
                icon={<Layers className="w-4 h-4" style={{ color: MID_GRAY.DEFAULT }} />}
                expanded={expandedSections.has('phases')}
                onToggle={() => toggleSection('phases')}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {phases.map((phase) => {
                    const phaseAudit = implementationAudits[phase.id] ?? phaseAudits[phase.id];
                    const elapsed = phaseTimingMap.get(phase.id);
                    const error = executionErrors[phase.id];
                    return (
                      <PhaseRow
                        key={phase.id}
                        phase={phase}
                        audit={phaseAudit}
                        elapsedMs={elapsed}
                        error={error}
                      />
                    );
                  })}
                </div>
              </CollapsibleSection>
            )}

            {/* Stage Timings */}
            {stageEntries.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div className="flex items-center gap-2" style={{ fontWeight: 600, fontSize: 14, color: '#1F2937', marginBottom: 8 }}>
                  <Clock className="w-4 h-4" style={{ color: MID_GRAY.DEFAULT }} />
                  Stage Timings
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {stageEntries.map(({ key, label, durationMs }) => (
                    <div key={key} className="flex items-center justify-between" style={{ fontSize: 12, padding: '3px 0' }}>
                      <span style={{ color: MID_GRAY.DEFAULT }}>{label}</span>
                      <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: '#1F2937' }}>
                        {formatDuration(durationMs)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Issues & Suggestions */}
            {finalAudit && issueCount > 0 && (
              <CollapsibleSection
                title={`Issues & Suggestions (${issueCount})`}
                sectionKey="issues"
                icon={<AlertOctagon className="w-4 h-4" style={{ color: MID_GRAY.DEFAULT }} />}
                expanded={expandedSections.has('issues')}
                onToggle={() => toggleSection('issues')}
              >
                {finalAudit.blockingIssues.length > 0 && (
                  <div style={{ background: ERROR.bg, borderRadius: 6, padding: '8px 12px', marginBottom: 8, fontSize: 12 }}>
                    <span style={{ fontWeight: 600 }}>Blocking Issues</span>
                    <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                      {finalAudit.blockingIssues.map((issue, i) => (
                        <li key={i} style={{ color: ERROR.DEFAULT, lineHeight: 1.5 }}>{issue}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {finalAudit.warnings.length > 0 && (
                  <div style={{ background: `${WARNING.DEFAULT}15`, borderRadius: 6, padding: '8px 12px', marginBottom: 8, fontSize: 12 }}>
                    <span style={{ fontWeight: 600 }}>Warnings</span>
                    <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                      {finalAudit.warnings.map((warn, i) => (
                        <li key={i} style={{ color: WARNING.DEFAULT, lineHeight: 1.5 }}>{warn}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {finalAudit.suggestions.length > 0 && (
                  <div style={{ background: `${FOREST_GREEN.DEFAULT}08`, borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>
                    <span style={{ fontWeight: 600 }}>Suggestions</span>
                    <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                      {finalAudit.suggestions.map((s, i) => (
                        <li key={i} style={{ color: MID_GRAY.DEFAULT, lineHeight: 1.5 }}>{s}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </CollapsibleSection>
            )}

          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
